import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import commaNumber from 'comma-number';
import Switch from 'react-switch';
import { prettyCurrency } from '../../Helpers/helpers';
import _ from 'lodash';
import cn from 'classnames';
import './BrandBudgetModal.scss';

import { sendBudgetInvoice } from '../../APIClient/budgets';

import { closeBrandBudgetModal } from '../../Actions/UIActions';
import { syncBrandBudget } from '../../Actions/BrandActions';

import {
  getBrand,
  getBrandBudgetRemaining,
  getBrandBudgetPending,
  getBrandBudgetIncreaseTakeRate,
  getBrandAllowedBudgetOverage
} from '../../Helpers/user_helpers';
import { getBrandBudgetModalStartingParams } from '../../Helpers/ui_helpers';

import Modal from '../General/Modal';

const BrandBudgetModal = props => {
  const { user, ui } = props;
  const brand = getBrand(user);
  const params = getBrandBudgetModalStartingParams(ui);
  const SHOPMY_TAKE_RATE = getBrandBudgetIncreaseTakeRate(user);
  const amountRequired = params?.amountRequired || 0;
  const amountRequiredPlusFees = amountRequired + (amountRequired * SHOPMY_TAKE_RATE) / 100;

  /* UI State Management */
  const [newBudgetAmount, setNewBudgetAmount] = useState(amountRequiredPlusFees || 1000);
  const [daysUntilDue, setDaysUntilDue] = useState(30);
  const [isAddingBudget, setIsAddingBudget] = useState(false);
  const [isOnCompletionPanel, setIsOnCompletionPanel] = useState(false);
  const [includeFeesInInput, setIncludeFeesInInput] = useState(false);

  /* UI Display */
  const budgetRemaining = getBrandBudgetRemaining(user);
  const allowedOverage = getBrandAllowedBudgetOverage(user);
  const budgetPending = getBrandBudgetPending(user);
  const onBudgetChange = e => setNewBudgetAmount(e.target.value.replace(/[^0-9.]/g, ''));
  const onDaysUntilDueChange = e => setDaysUntilDue(e.target.value.replace(/[^0-9]/g, ''));
  const close = () => props.closeBrandBudgetModal();
  const canAddBudget = !!brand.stripeCustomerId;
  const cannotAddBudgetReason = !brand.stripeCustomerId ? 'No Stripe Account Connected' : null;
  const newAmount = Number(newBudgetAmount);

  // If includeFeesInInput is true, then we want to compute the new budget to add so that it +SHOPMY_TAKE_RATE% totals to 'newAmount'
  // instead of adding +SHOPMY_RAKE_RATE% on top of 'newAmount'.
  const INVERSE_TAKE_RATE = 100 - 100 * 100 / (100 + SHOPMY_TAKE_RATE);
  const newBudgetFees = newAmount ? (
    Math.round(newAmount * (includeFeesInInput ? INVERSE_TAKE_RATE : SHOPMY_TAKE_RATE)) / 100
  ) : 0;
  const newBudgetToAddAfterFees = newAmount ? (
    Math.round(100 * (includeFeesInInput ? newAmount - newBudgetFees : newAmount)) / 100
  ) : 0;
  const newBudgetTotal = Math.round(100 * (budgetRemaining + newBudgetToAddAfterFees)) / 100;
  const totalBilled = includeFeesInInput ? Math.round(100 * newBudgetAmount) / 100 : Math.round(newBudgetAmount * (1 + SHOPMY_TAKE_RATE / 100) * 100) / 100;

  /* API Management */
  const confirmNewBudgetInvoice = async () => {
    if (isAddingBudget) return window.ALERT.error('Please wait while we process your request.');
    setIsAddingBudget(true);
    try {
      // Send the invoice
      const resp = await sendBudgetInvoice(
        _.omitBy(
          {
            Brand_id: brand.id,
            brand_amount: newBudgetToAddAfterFees,
            days_until_due: daysUntilDue || 30,
            sms_amount: newBudgetFees
          },
          _.isNil
        )
      );

      // Sync the budget so they can complete the action initially requested
      await props.syncBrandBudget();

      // Open the invoice in a new tab so they know it was sent
      window.open(resp.stripeInvoice?.hosted_invoice_url, '_blank');

      // Send them to the completion panel to remind them about payment requirements
      setIsOnCompletionPanel(true);
    } catch (e) {
      console.error(e);
      window.ALERT.error('There was an error adding funds to your budget. Please try again. If this continues, please contact support.');
    }
    setIsAddingBudget(false);
  };

  /* Data Syncing */
  React.useEffect(() => {
    props.syncBrandBudget();

    // Sync every 2 seconds while this panel is open in order to catch it if the brand makes a payment and returns
    const pollingSync = setInterval(() => document.hasFocus() && props.syncBrandBudget(), 2000);
    return () => clearInterval(pollingSync);
  }, []);

  return (
    <Modal
      visible
      smallForm
      fitContent
      close={close}
      className='brand-budget-modal-outer'
      innerClassName='brand-budget-modal-inner'
      contentClassName='brand-budget-modal-content'
    >
      <div className='modal-header'>
        <div className='header'>
          {isOnCompletionPanel
            ? 'Invoice Sent!'
            : amountRequired
              ? 'Insufficient Funds'
              : 'Increase your budget'}
        </div>
        <div className='subheader'>
          {isOnCompletionPanel
            ? 'Your invoice has been sent. Please pay the invoice in order for the funds to be deposited into your account.'
            : amountRequired
              ? _.isNil(brand.budget)
                ? `To perform this action you must first upload funds to your account.`
                : `Add more to your budget to continue.`
              : `Please specify how much you would like to add to your budget. Budget can be used for opportunities, bonuses and more.`}
        </div>
      </div>
      <div className='modal-body'>
        {isOnCompletionPanel ? (
          <div className='sections'>
            <div>
              <div className='label'>Budget Invoice Sent:</div>
              <div className='value'>{prettyCurrency(newBudgetAmount)}</div>
            </div>
            <div>
              <div className='label'>Pending Payment:</div>
              <div className='value'>{prettyCurrency(budgetPending)}</div>
            </div>
            <div>
              <div className='label'>Current Balance:</div>
              <div className='value'>{prettyCurrency(budgetRemaining)}</div>
            </div>
          </div>
        ) : (
          <>
            <div className='left-column'>
              <div className='section'>
                <div className='label'>Current Balance</div>
                <div className='value'>{prettyCurrency(budgetRemaining)}</div>
                {Boolean(allowedOverage) && <div className='label'>with ${prettyCurrency(allowedOverage)} allowed overage.</div>}
              </div>
              {!!amountRequired && (
                <div className='section'>
                  <div className='label'>Amount Required</div>
                  <div className='value'>{prettyCurrency(amountRequired)}</div>
                </div>
              )}
              {window.__ADMIN_CONTROL_MODE__ && (
                <div className='section'>
                  <div className='label'>Days Until Due:</div>
                  <input placeholder={30} value={daysUntilDue} onChange={onDaysUntilDueChange} className='payment-terms-input' />
                </div>
              )}
            </div>
            <div className='right-column'>
              <div className='right-column-top-section'>
                <div className='new-budget-row'>
                  <span className='label'>New Budget to Add</span>
                  <div className='new-budget-row-toggle-container'>
                    <span className='sublabel'>including fees</span>
                    <Switch
                      onChange={setIncludeFeesInInput}
                      checked={includeFeesInInput}
                      className='switch'
                      height={14}
                      width={24}
                      onColor='#11835a'
                      offColor='#777'
                      checkedIcon={false}
                      uncheckedIcon={false} />
                  </div>
                </div>
                <div className='new-budget-input-container'>
                  <input
                    autoFocus
                    // Can't use prettyBudgetString here
                    placeholder={commaNumber(Math.max(amountRequiredPlusFees, 1000))}
                    value={commaNumber(newBudgetAmount)}
                    onChange={onBudgetChange}
                    className='new-budget-input'
                  />
                  <div className='new-budget-dollar-sign'>$</div>
                </div>
                <div className='new-budget-row new-budget-fee-explanation'>
                  <span className='sublabel'>{`${SHOPMY_TAKE_RATE}% ShopMy Platform Fee`}</span>
                  <span className='sublabel'>{`${includeFeesInInput ? '-' : ''}${prettyCurrency(newBudgetFees)}`}</span>
                </div>
                <div className='new-budget-row new-budget-total-after-fees'>
                  <span className='label'>{includeFeesInInput ? 'Total Added to ShopMy Account' : 'Total Billed'}</span>
                  <span className='label'>{includeFeesInInput ? prettyCurrency(newBudgetToAddAfterFees) : prettyCurrency(totalBilled)}</span>
                </div>
                <div className='new-budget-due-date'>
                  <div className='sublabel'>{`${includeFeesInInput ? prettyCurrency(totalBilled) + ' due' : 'Due'} in ${daysUntilDue} days`}</div>
                </div>
              </div>
              <div className='new-budget-amount'>
                <div className='label'>New Balance</div>
                <div className='value'>{prettyCurrency(newBudgetTotal)}</div>
              </div>
            </div>
          </>
        )}
      </div>
      <div className='actions'>
        {isOnCompletionPanel ? (
          <div onClick={props.closeBrandBudgetModal} className='action primary'>
            Done
          </div>
        ) : (
          <>
            <div onClick={props.closeBrandBudgetModal} className='action secondary'>
              Cancel
            </div>
            {canAddBudget ? (
              <div onClick={confirmNewBudgetInvoice} className={cn('action', 'primary', { disabled: !newAmount })}>
                {isAddingBudget ? 'Sending...' : `Confirm`}
              </div>
            ) : (
              <div onClick={confirmNewBudgetInvoice} className='action primary disabled'>
                {cannotAddBudgetReason}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

BrandBudgetModal.propTypes = {
  user: PropTypes.object.isRequired,
  ui: PropTypes.object.isRequired,

  closeBrandBudgetModal: PropTypes.func.isRequired,
  syncBrandBudget: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { user, ui } = state;
  return { user, ui };
};

export default connect(mapStateToProps, {
  closeBrandBudgetModal,
  syncBrandBudget
})(BrandBudgetModal);
